export interface PokerTablePoint { x: number; y: number }
export interface PokerTableFootprint { minX: number; maxX: number; minY: number; maxY: number }
export interface PokerTableView { width: number; height: number }
export interface PokerSeatBankLayout {
  origin: PokerTablePoint;
  inward: PokerTablePoint;
  tangent: PokerTablePoint;
  tangentDirection: -1 | 1;
  handBounds?: PokerTableFootprint;
  bankFootprint: PokerTableFootprint;
  view: PokerTableView;
  gap?: number;
  emptyHandInset?: number;
  tangentOffset?: number;
}

export const POKER_TABLE_WORLD_SIZE = { width: 1_120, height: 680 } as const;
export const POKER_FELT_WORLD_SIZE = { width: 906, height: 466 } as const;
export const POKER_FELT_OBJECT_MARGIN = 10;

const clamp = (value: number, minimum: number, maximum: number) => minimum <= maximum ? Math.max(minimum, Math.min(maximum, value)) : (minimum + maximum) / 2;

/**
 * Keep an object's complete rendered footprint on the felt. Bounds are local
 * to the object's origin, so asymmetric leaning stacks remain fully visible.
 */
export function clampPokerFootprintToFelt(candidate: PokerTablePoint, footprint: PokerTableFootprint, view: PokerTableView, margin = POKER_FELT_OBJECT_MARGIN): PokerTablePoint {
  const feltHalfWidth = POKER_FELT_WORLD_SIZE.width * view.width / POKER_TABLE_WORLD_SIZE.width / 2;
  const feltHalfHeight = POKER_FELT_WORLD_SIZE.height * view.height / POKER_TABLE_WORLD_SIZE.height / 2;
  return {
    x: clamp(candidate.x, -feltHalfWidth + margin - footprint.minX, feltHalfWidth - margin - footprint.maxX),
    y: clamp(candidate.y, -feltHalfHeight + margin - footprint.minY, feltHalfHeight - margin - footprint.maxY),
  };
}

export function pokerFootprintAt(position: PokerTablePoint, footprint: PokerTableFootprint): PokerTableFootprint {
  return {
    minX: position.x + footprint.minX,
    maxX: position.x + footprint.maxX,
    minY: position.y + footprint.minY,
    maxY: position.y + footprint.maxY,
  };
}

export function pokerTableFootprintsOverlap(left: PokerTableFootprint, right: PokerTableFootprint, gap = 0) {
  return left.minX < right.maxX + gap && left.maxX > right.minX - gap
    && left.minY < right.maxY + gap && left.maxY > right.minY - gap;
}

function overlapArea(left: PokerTableFootprint, right: PokerTableFootprint, gap: number) {
  const width = Math.max(0, Math.min(left.maxX, right.maxX + gap) - Math.max(left.minX, right.minX - gap));
  const height = Math.max(0, Math.min(left.maxY, right.maxY + gap) - Math.max(left.minY, right.minY - gap));
  return width * height;
}

/**
 * Give every seat two physical lanes: private cards beside the rail and the
 * player's chip bank farther inward. The fallback side candidates protect the
 * separation when a large bank is clamped against a felt edge.
 */
export function placePokerSeatBankClearOfHand({
  origin,
  inward,
  tangent,
  tangentDirection,
  handBounds,
  bankFootprint,
  view,
  gap = 14,
  emptyHandInset = 104,
  tangentOffset = 42,
}: PokerSeatBankLayout): PokerTablePoint {
  const base = {
    x: origin.x + inward.x * emptyHandInset,
    y: origin.y + inward.y * emptyHandInset,
  };
  if (!handBounds) {
    return clampPokerFootprintToFelt({
      x: base.x + tangent.x * tangentDirection * tangentOffset,
      y: base.y + tangent.y * tangentDirection * tangentOffset,
    }, bankFootprint, view);
  }

  const beyond = (axis: "inward" | "tangent", direction: -1 | 1, offset = 0) => {
    const vector = axis === "inward" ? inward : tangent;
    const candidate = axis === "inward"
      ? { x: base.x + tangent.x * tangentDirection * offset, y: base.y + tangent.y * tangentDirection * offset }
      : { ...base };
    const signedX = vector.x * direction; const signedY = vector.y * direction;
    if (signedX > .5) candidate.x = handBounds.maxX + gap - bankFootprint.minX;
    else if (signedX < -.5) candidate.x = handBounds.minX - gap - bankFootprint.maxX;
    if (signedY > .5) candidate.y = handBounds.maxY + gap - bankFootprint.minY;
    else if (signedY < -.5) candidate.y = handBounds.minY - gap - bankFootprint.maxY;
    return clampPokerFootprintToFelt(candidate, bankFootprint, view);
  };

  const candidates = [
    beyond("inward", 1, tangentOffset),
    beyond("inward", 1),
    beyond("tangent", tangentDirection),
    beyond("tangent", tangentDirection === 1 ? -1 : 1),
  ];
  const clear = candidates.find((candidate) => !pokerTableFootprintsOverlap(pokerFootprintAt(candidate, bankFootprint), handBounds, gap));
  if (clear) return clear;
  return candidates.sort((left, right) => overlapArea(pokerFootprintAt(left, bankFootprint), handBounds, gap) - overlapArea(pokerFootprintAt(right, bankFootprint), handBounds, gap))[0];
}
